/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 * Products Tab - Modular component for product management with industry categories
 */

import React, { useMemo, useState, useEffect } from 'react';
import { CityStats, Grid, BuildingType, Product } from '../../../types';
import { PRODUCTS, BUILDINGS } from '../../../constants';
import { FINTECH_PRODUCTS, HEALTHTECH_PRODUCTS, SAAS_PRODUCTS, INSURTECH_PRODUCTS, EDTECH_PRODUCTS, RETAIL_PRODUCTS, INDUSTRY_CATEGORIES } from '../../../constants/industryCategories';
import { playUiSfx } from '../../../utils/uiAudio';

interface ProductsTabProps {
  stats: CityStats;
  grid: Grid;
  onLaunchProduct: (id: string) => void;
}

// Convert industry products to standard Product format
const convertIndustryProduct = (prod: any): Product => ({
  id: prod.id,
  name: prod.name,
  description: prod.description,
  revenue: prod.revenue,
  risk: prod.risk,
  requirements: prod.requirements || []
});

const ProductsTab: React.FC<ProductsTabProps> = ({ stats, grid, onLaunchProduct }) => {
  const [activeCategory, setActiveCategory] = useState<string>('all');
  const [industryCategory, setIndustryCategory] = useState<string>('sandbox');

  // Load industry category from localStorage
  useEffect(() => {
    const savedCategory = localStorage.getItem('compliance_command_industry');
    if (savedCategory) {
      setIndustryCategory(savedCategory);
      setActiveCategory(savedCategory === 'sandbox' ? 'all' : savedCategory);
    }
  }, []);

  // Memoize building counts
  const counts = useMemo(() => {
    const counts: Record<string, number> = {};
    grid.flat().forEach(tile => {
      if (tile.buildingType !== BuildingType.None && tile.isConnected) {
        counts[tile.buildingType] = (counts[tile.buildingType] || 0) + 1;
      }
    });
    return counts;
  }, [grid]);

  // Get all products including industry-specific ones
  const allProducts = useMemo(() => {
    const baseProducts = PRODUCTS.map(p => ({ ...p, category: 'core' }));
    const fintechProducts = FINTECH_PRODUCTS.map(p => ({ ...convertIndustryProduct(p), category: 'fintech' }));
    const healthtechProducts = HEALTHTECH_PRODUCTS.map(p => ({ ...convertIndustryProduct(p), category: 'healthtech' }));
    const saasProducts = SAAS_PRODUCTS.map(p => ({ ...convertIndustryProduct(p), category: 'saas_b2b' }));
    const insurtechProducts = INSURTECH_PRODUCTS.map(p => ({ ...convertIndustryProduct(p), category: 'insurtech' }));
    const edtechProducts = EDTECH_PRODUCTS.map(p => ({ ...convertIndustryProduct(p), category: 'edtech' }));
    const retailProducts = RETAIL_PRODUCTS.map(p => ({ ...convertIndustryProduct(p), category: 'retail_ecom' }));
    
    return [...baseProducts, ...fintechProducts, ...healthtechProducts, ...saasProducts, ...insurtechProducts, ...edtechProducts, ...retailProducts];
  }, []);

  // Filter products based on active category
  const filteredProducts = useMemo(() => {
    if (activeCategory === 'all') return allProducts;
    if (activeCategory === 'core') return allProducts.filter(p => p.category === 'core');
    return allProducts.filter(p => p.category === activeCategory || p.category === 'core');
  }, [allProducts, activeCategory]);

  // Calculate product metrics
  const productMetrics = useMemo(() => {
    const activeCount = stats.activeProducts.length;
    const totalRevenue = stats.activeProducts.reduce((sum, prodId) => {
      const prod = allProducts.find(p => p.id === prodId);
      return sum + (prod?.revenue || 0);
    }, 0);
    const totalRisk = stats.activeProducts.reduce((sum, prodId) => {
      const prod = allProducts.find(p => p.id === prodId);
      return sum + (prod?.risk || 0);
    }, 0);
    return { activeCount, totalRevenue, totalRisk };
  }, [stats.activeProducts, allProducts]);

  // Category info for display
  const categoryInfo: Record<string, { name: string; color: string; emoji: string }> = {
    all: { name: 'All Products', color: '#6366f1', emoji: '📦' },
    core: { name: 'Core Products', color: '#f59e0b', emoji: '🏛️' },
    fintech: { name: 'FinTech', color: '#10B981', emoji: '💳' },
    healthtech: { name: 'HealthTech', color: '#3B82F6', emoji: '🏥' },
    saas_b2b: { name: 'B2B Enterprise', color: '#8B5CF6', emoji: '💼' },
    insurtech: { name: 'InsurTech', color: '#6366F1', emoji: '🛡️' },
    edtech: { name: 'EdTech', color: '#EC4899', emoji: '🎓' },
    retail_ecom: { name: 'Retail & E-com', color: '#F59E0B', emoji: '🛒' }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="border-b border-white/10 pb-4">
        <h3 className="text-xl font-bold text-white uppercase tracking-wider">Product Portfolio</h3>
        <p className="text-slate-400 mt-2">Launch and manage revenue-generating products across industries</p>
      </div>

      {/* Category Filter Tabs */}
      <div className="flex flex-wrap gap-2">
        {Object.entries(categoryInfo).map(([key, info]) => (
          <button
            key={key}
            onClick={() => { playUiSfx('click'); setActiveCategory(key); }}
            className={`px-4 py-2 rounded-lg text-sm font-bold transition-all flex items-center gap-2 ${
              activeCategory === key
                ? 'text-white shadow-lg'
                : 'bg-white/5 text-slate-400 hover:bg-white/10 hover:text-white border border-white/10'
            }`}
            style={activeCategory === key ? { backgroundColor: info.color } : {}}
          >
            <span>{info.emoji}</span>
            <span>{info.name}</span>
            <span className="text-xs opacity-70">
              ({allProducts.filter(p => key === 'all' ? true : key === 'core' ? p.category === 'core' : p.category === key || p.category === 'core').length})
            </span>
          </button>
        ))}
      </div>

      {/* Product Metrics Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-gradient-to-br from-indigo-900/30 to-purple-900/30 border border-indigo-700/30 rounded-lg p-4">
          <div className="text-xs text-indigo-400 uppercase mb-1">Active Products</div>
          <div className="text-3xl font-black text-white">{productMetrics.activeCount}</div>
          <div className="text-xs text-slate-400 mt-1">of {allProducts.length} available</div>
        </div>
        <div className="bg-gradient-to-br from-emerald-900/30 to-green-900/30 border border-emerald-700/30 rounded-lg p-4">
          <div className="text-xs text-emerald-400 uppercase mb-1">Revenue/Tick</div>
          <div className="text-3xl font-black text-emerald-400">+${productMetrics.totalRevenue}</div>
          <div className="text-xs text-slate-400 mt-1">from all products</div>
        </div>
        <div className="bg-gradient-to-br from-red-900/30 to-orange-900/30 border border-red-700/30 rounded-lg p-4">
          <div className="text-xs text-red-400 uppercase mb-1">Compliance Risk</div>
          <div className="text-3xl font-black text-red-400">-{productMetrics.totalRisk}%</div>
          <div className="text-xs text-slate-400 mt-1">per tick drain</div>
        </div>
      </div>

      {/* Product Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {filteredProducts.map(prod => {
          const isLaunched = stats.activeProducts.includes(prod.id);
          const canLaunch = prod.requirements.every(req => (counts[req.type] || 0) >= req.count);
          const requirementsMet = prod.requirements.filter(req => (counts[req.type] || 0) >= req.count).length;
          const totalRequirements = prod.requirements.length;

          return (
            <div 
              key={prod.id} 
              className={`p-5 rounded-lg border flex flex-col justify-between transition-all ${
                isLaunched 
                  ? 'bg-indigo-950/20 border-indigo-500/50 shadow-[0_0_15px_rgba(99,102,241,0.2)]' 
                  : canLaunch
                    ? 'bg-emerald-950/10 border-emerald-700/30 hover:border-emerald-500/50'
                    : 'bg-white/5 border-white/10'
              }`}
            >
              <div>
                {/* Product Header */}
                <div className="flex justify-between items-start mb-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-bold text-white text-lg">{prod.name}</h3>
                      {prod.category && prod.category !== 'core' && (
                        <span 
                          className="px-2 py-0.5 rounded text-[10px] font-bold"
                          style={{ 
                            backgroundColor: `${categoryInfo[prod.category]?.color || '#666'}20`,
                            color: categoryInfo[prod.category]?.color || '#666'
                          }}
                        >
                          {categoryInfo[prod.category]?.emoji} {categoryInfo[prod.category]?.name}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-slate-400 mt-1">{prod.description}</p>
                  </div>
                  {isLaunched && (
                    <span className="px-3 py-1 bg-indigo-500 text-white text-xs font-bold rounded-full animate-pulse shrink-0">
                      LIVE
                    </span>
                  )}
                </div>

                {/* Revenue/Risk Stats */}
                <div className="flex gap-4 text-sm font-mono mb-4 p-3 bg-black/30 rounded-lg">
                  <div className="flex items-center gap-2">
                    <span className="text-emerald-400">💰</span>
                    <span className="text-emerald-400 font-bold">+${prod.revenue}/tick</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-rose-400">⚠️</span>
                    <span className="text-rose-400 font-bold">-{prod.risk}% comp</span>
                  </div>
                </div>

                {/* Requirements */}
                <div className="mb-4">
                  <div className="text-xs text-slate-500 uppercase font-bold mb-2">
                    Requirements ({requirementsMet}/{totalRequirements})
                  </div>
                  <div className="space-y-2">
                    {prod.requirements.map(req => {
                      const has = counts[req.type] || 0;
                      const met = has >= req.count;
                      const building = BUILDINGS[req.type];
                      return (
                        <div 
                          key={req.type} 
                          className={`flex justify-between items-center p-2 rounded ${
                            met ? 'bg-emerald-900/20 border border-emerald-700/30' : 'bg-red-900/10 border border-red-700/30'
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            <div 
                              className="w-3 h-3 rounded-sm" 
                              style={{ backgroundColor: building.color }}
                            />
                            <span className={`text-sm ${met ? 'text-emerald-400' : 'text-red-400'}`}>
                              {building.name}
                            </span>
                          </div>
                          <span className={`text-sm font-mono ${met ? 'text-emerald-400' : 'text-red-400'}`}>
                            {has}/{req.count} {met ? '✓' : '✗'}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Launch Button */}
              <button 
                onClick={() => { 
                  if (!isLaunched && canLaunch) {
                    playUiSfx('success'); 
                    onLaunchProduct(prod.id); 
                  }
                }} 
                disabled={isLaunched || !canLaunch}
                className={`w-full py-3 rounded-lg text-sm font-bold uppercase tracking-widest transition-all ${
                  isLaunched 
                    ? 'bg-indigo-600/50 text-indigo-200 cursor-default'
                    : canLaunch 
                      ? 'bg-emerald-600 hover:bg-emerald-500 text-white shadow-lg hover:shadow-emerald-500/25' 
                      : 'bg-slate-800 text-slate-500 cursor-not-allowed'
                }`}
              >
                {isLaunched ? '✓ Deployed & Generating Revenue' : canLaunch ? '🚀 Launch Product' : '🔒 Missing Requirements'}
              </button>
            </div>
          );
        })}
      </div>

      {/* Tips Section */}
      <div className="bg-slate-900/30 border border-white/10 rounded-lg p-4">
        <h4 className="text-sm font-bold text-cyan-400 uppercase mb-3">💡 Product Strategy Tips</h4>
        <ul className="text-sm text-slate-400 space-y-2">
          <li>• <strong>Digital Checking</strong> is the easiest to launch - requires only AML Unit and Support</li>
          <li>• <strong>Crypto Trading</strong> generates the most revenue but has highest compliance risk</li>
          <li>• Products with <strong>Internet Gateway</strong> get boosted revenue from network effects</li>
          <li>• Balance revenue generation with compliance to avoid regulatory issues</li>
        </ul>
      </div>
    </div>
  );
};

export default ProductsTab;
